import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import {
  CreateImporterDto,
  GetImporterQueryDto,
  GetImporterResponseDto,
  GetImportersResponseDto,
  ImporterDto,
  UpdateImporterDto,
} from "@/types/api/api-types";

//#region Get

const getAllImporters = async (query: GetImporterQueryDto) => {
  const response = await axiosApi.get<GetImportersResponseDto>("v1/importer", { params: query });
  return response.data;
};

export const useGetAllImporters = (query: GetImporterQueryDto = {}) => {
  return useQuery({
    queryKey: ["importer", "all", query],
    queryFn: () => getAllImporters(query),
  });
};

const getOneImporter = async (id: string) => {
  const response = await axiosApi.get<GetImporterResponseDto>(`v1/importer/${id}`);
  return response.data;
};

export const useGetOneImporter = (id: string) => {
  return useQuery({
    queryKey: ["importer", id],
    queryFn: () => getOneImporter(id),
    enabled: !!id,
  });
};

//#endregion

//#region Post

const createImporter = async (payload: CreateImporterDto) => {
  return axiosApi.post<ImporterDto>("v1/importer", payload);
};

export const useCreateImporter = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createImporter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["importer", "all"] });
      toast.success(t("importerApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("importerApi.createError"));
    },
  });
};

//#endregion

//#region Patch

const updateImporter = async ({ id, payload }: { id: string; payload: UpdateImporterDto }) => {
  return axiosApi.patch<ImporterDto>(`v1/importer/${id}`, payload);
};

export const useUpdateImporter = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateImporter,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["importer", "all"] });
      queryClient.invalidateQueries({ queryKey: ["importer", res.data.id] });
      toast.success(t("importerApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("importerApi.updateError"));
    },
  });
};

//#endregion

//#region Delete

const deleteImporter = async (id: string) => {
  return axiosApi.delete(`v1/importer/${id}`);
};

export const useDeleteImporter = () => {
  const { t } = useTranslation("boCatalog");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImporter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["importer", "all"] });
      toast.success(t("importerApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("importerApi.deleteError"));
    },
  });
};

//#endregion
